import { useState, useEffect, useCallback } from 'react';
import { reportsAPI } from '@/lib/api';
import { useSettings } from '@/contexts/SettingsContext';
import {
  TrendingUp, Users, DollarSign, Target, Download, FileSpreadsheet, FileText
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip, Cell, PieChart, Pie
} from 'recharts';

const RANGES = [
  { value: '7d', label: '7 Days' },
  { value: '30d', label: '30 Days' },
  { value: '90d', label: '90 Days' },
  { value: 'all', label: 'All Time' },
];

const SOURCE_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#06B6D4', '#F97316', '#6B7280'];

function ExportButtons({ reportType, range, token }) {
  const excelUrl = reportsAPI.exportExcel(reportType, { time_range: range });
  const pdfUrl = reportsAPI.exportPDF(reportType, { time_range: range });

  const download = async (url, ext) => {
    const t = localStorage.getItem('token');
    const res = await fetch(url, { headers: { Authorization: `Bearer ${t}` }, credentials: 'include' });
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${reportType}_${range}.${ext}`;
    a.click();
  };

  return (
    <div className="flex gap-2">
      <Button variant="outline" size="sm" className="gap-1.5 text-xs h-8" onClick={() => download(excelUrl, 'xlsx')} data-testid={`export-excel-${reportType}`}>
        <FileSpreadsheet className="w-3.5 h-3.5 text-green-600" /> Excel
      </Button>
      <Button variant="outline" size="sm" className="gap-1.5 text-xs h-8" onClick={() => download(pdfUrl, 'pdf')} data-testid={`export-pdf-${reportType}`}>
        <FileText className="w-3.5 h-3.5 text-red-500" /> PDF
      </Button>
    </div>
  );
}

function MetricCard({ label, value, sub, icon: Icon, color = 'primary' }) {
  return (
    <div className="bg-muted/30 rounded-lg p-4 border border-border/40">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">{label}</p>
          <p className="text-2xl font-bold mt-1">{value}</p>
          {sub && <p className="text-[10px] text-muted-foreground mt-0.5">{sub}</p>}
        </div>
        {Icon && <Icon className="w-5 h-5 text-muted-foreground/50" />}
      </div>
    </div>
  );
}

export default function ReportsPage() {
  const [range, setRange] = useState('30d');
  const [convData, setConvData] = useState(null);
  const [agentData, setAgentData] = useState(null);
  const [revData, setRevData] = useState(null);
  const [sourceData, setSourceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('conversion');
  const { stageMap, formatCurrency } = useSettings();
  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = { time_range: range };
      const [c, a, r, s] = await Promise.all([
        reportsAPI.leadConversion(params),
        reportsAPI.agentPerformance(params),
        reportsAPI.revenue(params),
        reportsAPI.leadSourceROI(params),
      ]);
      setConvData(c.data); setAgentData(a.data); setRevData(r.data); setSourceData(s.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [range]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const rangeLabel = RANGES.find(r => r.value === range)?.label || range;

  return (
    <div className="space-y-5 fade-in max-w-6xl" data-testid="reports-page">
      <div className="flex items-start sm:items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Rapporten</h1>
          <p className="text-muted-foreground text-xs sm:text-sm">Analytics and performance insights</p>
        </div>
        <div className="flex items-center gap-1.5 bg-muted/50 rounded-lg p-1">
          {RANGES.map((r) => (
            <button key={r.value} onClick={() => setRange(r.value)} data-testid={`report-range-${r.value}`}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${range === r.value ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[...Array(4)].map((_, i) => <div key={i} className="h-48 bg-muted animate-pulse rounded-lg" />)}
        </div>
      ) : (
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="conversion" className="gap-1.5 text-xs"><TrendingUp className="w-3.5 h-3.5" />Conversie</TabsTrigger>
            <TabsTrigger value="agents" className="gap-1.5 text-xs"><Users className="w-3.5 h-3.5" />Medewerkers</TabsTrigger>
            <TabsTrigger value="revenue" className="gap-1.5 text-xs"><DollarSign className="w-3.5 h-3.5" />Omzet</TabsTrigger>
            <TabsTrigger value="sources" className="gap-1.5 text-xs"><Target className="w-3.5 h-3.5" />Sources</TabsTrigger>
          </TabsList>

          {/* ─── Lead Conversion ─── */}
          <TabsContent value="conversion" className="mt-4 space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-sm font-semibold">Lead Conversie Funnel</h3>
              <ExportButtons reportType="lead-conversion" range={range} />
            </div>
            {convData && (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <MetricCard label="Total Leads" value={convData.total_leads} icon={Users} />
                  <MetricCard label="Win Rate" value={`${convData.win_rate}%`} sub={`${convData.won} won / ${convData.lost} lost`} icon={TrendingUp} />
                  <MetricCard label="New in Range" value={convData.new_in_range} sub={rangeLabel} icon={Target} />
                  <MetricCard label="Won Deals" value={convData.won} icon={DollarSign} />
                </div>
                <Card className="border-border/60">
                  <CardContent className="pt-4">
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={convData.stages} margin={{ left: 0, right: 16 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                        <XAxis dataKey="stage" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} tickFormatter={(v) => stageMap[v]?.label || v.charAt(0).toUpperCase() + v.slice(1)} />
                        <YAxis tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} allowDecimals={false} />
                        <RechartsTooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px', fontSize: '12px' }} />
                        <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                          {(convData.stages || []).map((s, i) => <Cell key={i} fill={stageMap[s.stage]?.color || '#6B7280'} />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
                {/* Stage breakdown table */}
                <Card className="border-border/60 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b bg-muted/30">
                      <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Stage</th>
                      <th className="text-right px-4 py-2.5 font-medium text-muted-foreground">Count</th>
                      <th className="text-right px-4 py-2.5 font-medium text-muted-foreground">% of Total</th>
                      <th className="px-4 py-2.5 font-medium text-muted-foreground w-48"></th>
                    </tr></thead>
                    <tbody>
                      {convData.stages.map((s) => (
                        <tr key={s.stage} className="border-b border-border/30">
                          <td className="px-4 py-2.5 flex items-center gap-2">
                            <div className="w-3 h-3 rounded-sm" style={{ background: stageMap[s.stage]?.color }} />
                            <span className="font-medium capitalize">{stageMap[s.stage]?.label || s.stage}</span>
                          </td>
                          <td className="text-right px-4 py-2.5 mono">{s.count}</td>
                          <td className="text-right px-4 py-2.5">{s.percentage}%</td>
                          <td className="px-4 py-2.5">
                            <div className="w-full bg-muted rounded-full h-2">
                              <div className="h-2 rounded-full transition-all" style={{ width: `${s.percentage}%`, background: stageMap[s.stage]?.color }} />
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Card>
              </>
            )}
          </TabsContent>

          {/* ─── Agent Performance ─── */}
          <TabsContent value="agents" className="mt-4 space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-sm font-semibold">Medewerker Prestaties</h3>
              <ExportButtons reportType="agent-performance" range={range} />
            </div>
            {agentData && (
              <>
                <Card className="border-border/60 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="border-b bg-muted/30">
                        <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Medewerker</th>
                        <th className="text-center px-3 py-2.5 font-medium text-muted-foreground">Leads</th>
                        <th className="text-center px-3 py-2.5 font-medium text-muted-foreground">Won</th>
                        <th className="text-center px-3 py-2.5 font-medium text-muted-foreground">Lost</th>
                        <th className="text-center px-3 py-2.5 font-medium text-muted-foreground">Conv.</th>
                        <th className="text-center px-3 py-2.5 font-medium text-muted-foreground">Activities</th>
                        <th className="text-right px-4 py-2.5 font-medium text-muted-foreground">Revenue</th>
                      </tr></thead>
                      <tbody>
                        {agentData.agents.map((a, i) => (
                          <tr key={a.user_id} className="border-b border-border/30 hover:bg-muted/20">
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-[10px] font-bold text-primary">{a.name?.charAt(0)}</div>
                                <div>
                                  <p className="font-medium text-xs">{a.name}</p>
                                  <p className="text-[10px] text-muted-foreground capitalize">{a.role_name || a.role}</p>
                                </div>
                              </div>
                            </td>
                            <td className="text-center px-3 py-3">{a.total_leads}</td>
                            <td className="text-center px-3 py-3 font-medium text-green-600">{a.won}</td>
                            <td className="text-center px-3 py-3 text-red-500">{a.lost}</td>
                            <td className="text-center px-3 py-3">
                              <Badge variant={a.conversion_rate >= 50 ? 'default' : 'secondary'} className="text-[10px]">{a.conversion_rate}%</Badge>
                            </td>
                            <td className="text-center px-3 py-3">{a.activities}</td>
                            <td className="text-right px-4 py-3 font-medium mono">{formatCurrency(a.revenue)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
                {/* Agent chart */}
                <Card className="border-border/60">
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Won Deals by Agent</CardTitle></CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={agentData.agents.filter(a => a.won > 0)} margin={{ left: 0, right: 16 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                        <XAxis dataKey="name" tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} />
                        <YAxis tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} allowDecimals={false} />
                        <RechartsTooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px', fontSize: '12px' }} />
                        <Bar dataKey="won" fill="#22C55E" radius={[4, 4, 0, 0]} name="Won" />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>

          {/* ─── Revenue ─── */}
          <TabsContent value="revenue" className="mt-4 space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-sm font-semibold">Revenue Report</h3>
              <ExportButtons reportType="revenue" range={range} />
            </div>
            {revData && (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <MetricCard label="Closed Revenue" value={formatCurrency(revData.total_revenue)} icon={DollarSign} />
                  <MetricCard label="Pipeline Value" value={formatCurrency(revData.pipeline_value)} icon={TrendingUp} />
                  <MetricCard label="Won Deals" value={revData.won_deals} icon={Target} />
                  <MetricCard label="Avg Deal Size" value={formatCurrency(revData.avg_deal_size)} icon={DollarSign} />
                </div>
                <Card className="border-border/60">
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Pipeline Value by Stage</CardTitle></CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={240}>
                      <BarChart data={revData.stage_values} margin={{ left: 20, right: 16 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                        <XAxis dataKey="stage" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} tickFormatter={(v) => stageMap[v]?.label || v.charAt(0).toUpperCase() + v.slice(1)} />
                        <YAxis tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                        <RechartsTooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px', fontSize: '12px' }} formatter={(v) => formatCurrency(v)} />
                        <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                          {(revData.stage_values || []).map((s, i) => <Cell key={i} fill={stageMap[s.stage]?.color || '#6B7280'} />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>

          {/* ─── Lead Source ROI ─── */}
          <TabsContent value="sources" className="mt-4 space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-sm font-semibold">Lead Source ROI</h3>
              <ExportButtons reportType="lead-source-roi" range={range} />
            </div>
            {sourceData && (
              <>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <Card className="border-border/60">
                    <CardHeader className="pb-2"><CardTitle className="text-sm">Volume by Source</CardTitle></CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={240}>
                        <PieChart>
                          <Pie data={sourceData.sources} dataKey="total_leads" nameKey="source" cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={2} label={({ source, total_leads }) => `${source?.replace(/_/g, ' ')} (${total_leads})`} labelLine={false}>
                            {sourceData.sources.map((_, i) => <Cell key={i} fill={SOURCE_COLORS[i % SOURCE_COLORS.length]} />)}
                          </Pie>
                          <RechartsTooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px', fontSize: '12px' }} />
                        </PieChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                  <Card className="border-border/60 overflow-hidden">
                    <table className="w-full text-sm">
                      <thead><tr className="border-b bg-muted/30">
                        <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Source</th>
                        <th className="text-center px-3 py-2.5 font-medium text-muted-foreground">Leads</th>
                        <th className="text-center px-3 py-2.5 font-medium text-muted-foreground">Won</th>
                        <th className="text-center px-3 py-2.5 font-medium text-muted-foreground">Conv.</th>
                        <th className="text-right px-4 py-2.5 font-medium text-muted-foreground">Revenue</th>
                      </tr></thead>
                      <tbody>
                        {sourceData.sources.map((s, i) => (
                          <tr key={s.source} className="border-b border-border/30">
                            <td className="px-4 py-2.5 flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full" style={{ background: SOURCE_COLORS[i % SOURCE_COLORS.length] }} />
                              <span className="capitalize text-xs">{s.source?.replace(/_/g, ' ')}</span>
                            </td>
                            <td className="text-center px-3 py-2.5">{s.total_leads}</td>
                            <td className="text-center px-3 py-2.5 text-green-600 font-medium">{s.won}</td>
                            <td className="text-center px-3 py-2.5"><Badge variant={s.conversion_rate >= 50 ? 'default' : 'secondary'} className="text-[10px]">{s.conversion_rate}%</Badge></td>
                            <td className="text-right px-4 py-2.5 mono font-medium">{formatCurrency(s.revenue)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Card>
                </div>
              </>
            )}
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
